import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { FileText, Mail, Phone, MapPin, Building } from "lucide-react";

const Impressum = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Impressum – Malerfachbetrieb Stein Herford"
        description="Impressum und rechtliche Angaben der Malerfachbetrieb Stein GmbH & Co. KG in Herford."
        canonical="https://malerfachbetrieb-stein.de/impressum"
      />
      <Navigation />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-16 text-center animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 border border-foreground/20 bg-foreground/5 rounded-full">
                <FileText className="h-4 w-4" />
                <span className="text-xs tracking-widest uppercase">Rechtliche Angaben</span>
              </div>
              <h1 className="font-caslon text-4xl md:text-6xl font-bold mb-6">
                Impressum
              </h1>
              <div className="w-24 h-px bg-foreground mx-auto" />
            </div>

            {/* Company Information */}
            <div className="bg-muted/30 rounded-3xl p-8 md:p-12 mb-8 animate-fade-in-up delay-200">
              <h2 className="font-caslon text-2xl font-bold mb-6 flex items-center gap-3">
                <Building className="h-6 w-6" />
                Angaben gemäß § 5 DDG
              </h2>
              
              <div className="space-y-4 text-foreground">
                <p className="font-semibold text-lg">Malerfachbetrieb Stein GmbH und Co. KG</p>
                
                <div className="flex items-start gap-3 text-muted-foreground">
                  <MapPin className="h-5 w-5 mt-1 flex-shrink-0" />
                  <div>
                    <p>Mindener Str. 183</p>
                    <p>32049 Herford</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="h-5 w-5 flex-shrink-0" />
                  <div>
                    <p>Tel.: 05221 / 3832130</p>
                    <p>Fax: 05262 / 6257930</p>
                    <p>Handy: 0178 / 8783369</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="h-5 w-5 flex-shrink-0" />
                  <a href="mailto:info@malerfachbetrieb-stein.de" className="hover:text-foreground transition-colors">
                    info@malerfachbetrieb-stein.de
                  </a>
                </div>

                <div className="pt-6 mt-6 border-t border-border space-y-2">
                  <p><strong>Registergericht:</strong> Amtsgericht Bad Oeynhausen HRA 10628</p>
                  <p><strong>Umsatzsteuer-Identifikationsnummer:</strong> DE316255337</p>
                  <p><strong>Steuer-Nr.:</strong> 329/5900/0577</p>
                </div>

                <div className="pt-6 mt-6 border-t border-border">
                  <p className="font-semibold mb-2">Vertreten durch:</p>
                  <p>Malerfachbetrieb Stein Verwaltungs GmbH</p>
                  <p className="text-sm">Registergericht: Amtsgericht Bad Oeynhausen HRB 20265</p>
                  <p className="mt-3 text-sm">Diese wird vertreten durch: <strong>Alexander Stein</strong></p>
                </div>
              </div>
            </div>

            {/* Legal Sections */}
            <div className="space-y-8 animate-fade-in-up delay-400">
              {/* Haftung für Inhalte */}
              <div className="bg-background border border-foreground/10 rounded-3xl p-8">
                <h2 className="font-caslon text-xl font-bold mb-4">Haftung für Inhalte</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, 
                  Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. 
                  Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten 
                  nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als 
                  Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde 
                  Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige 
                  Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von 
                  Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche 
                  Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung 
                  möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte 
                  umgehend entfernen.
                </p>
              </div>

              {/* Haftung für Links */}
              <div className="bg-background border border-foreground/10 rounded-3xl p-8">
                <h2 className="font-caslon text-xl font-bold mb-4">Haftung für Links</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen 
                  Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. 
                  Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der 
                  Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche 
                  Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht 
                  erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete 
                  Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen 
                  werden wir derartige Links umgehend entfernen.
                </p>
              </div>

              {/* Urheberrecht */}
              <div className="bg-background border border-foreground/10 rounded-3xl p-8">
                <h2 className="font-caslon text-xl font-bold mb-4">Urheberrecht</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem 
                  deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der 
                  Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des 
                  jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, 
                  nicht kommerziellen Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber 
                  erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter 
                  als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, 
                  bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir 
                  derartige Inhalte umgehend entfernen.
                </p>
              </div>

              {/* Datenschutz */}
              <div className="bg-background border border-foreground/10 rounded-3xl p-8">
                <h2 className="font-caslon text-xl font-bold mb-4">Datenschutz</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Die Nutzung unserer Webseite ist in der Regel ohne Angabe personenbezogener Daten möglich. 
                  Soweit auf unseren Seiten personenbezogene Daten (beispielsweise Name, Anschrift oder E-Mail-Adressen) 
                  erhoben werden, erfolgt dies, soweit möglich, stets auf freiwilliger Basis. Diese Daten werden ohne 
                  Ihre ausdrückliche Zustimmung nicht an Dritte weitergegeben.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Wir weisen darauf hin, dass die Datenübertragung im Internet (z.B. bei der Kommunikation per E-Mail) 
                  Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist 
                  nicht möglich.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten durch Dritte zur 
                  Übersendung von nicht ausdrücklich angeforderter Werbung und Informationsmaterialien wird hiermit 
                  ausdrücklich widersprochen. Die Betreiber der Seiten behalten sich ausdrücklich rechtliche Schritte 
                  im Falle der unverlangten Zusendung von Werbeinformationen, etwa durch Spam-Mails, vor.
                </p>
              </div>

              {/* Google Analytics */}
              <div className="bg-background border border-foreground/10 rounded-3xl p-8">
                <h2 className="font-caslon text-xl font-bold mb-4">Google Analytics</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Diese Website benutzt Google Analytics, einen Webanalysedienst der Google Inc. ("Google"). 
                  Google Analytics verwendet sog. "Cookies", Textdateien, die auf Ihrem Computer gespeichert werden 
                  und die eine Analyse der Benutzung der Website durch Sie ermöglicht. Die durch den Cookie erzeugten 
                  Informationen über Ihre Benutzung dieser Website (einschließlich Ihrer IP-Adresse) wird an einen 
                  Server von Google in den USA übertragen und dort gespeichert. Google wird diese Informationen benutzen, 
                  um Ihre Nutzung der Website auszuwerten, um Reports über die Websiteaktivitäten für die Websitebetreiber 
                  zusammenzustellen und um weitere mit der Websitenutzung und der Internetnutzung verbundene 
                  Dienstleistungen zu erbringen. Auch wird Google diese Informationen gegebenenfalls an Dritte übertragen, 
                  sofern dies gesetzlich vorgeschrieben oder soweit Dritte diese Daten im Auftrag von Google verarbeiten. 
                  Google wird in keinem Fall Ihre IP-Adresse mit anderen Daten der Google in Verbindung bringen. Sie können 
                  die Installation der Cookies durch eine entsprechende Einstellung Ihrer Browser Software verhindern; wir 
                  weisen Sie jedoch darauf hin, dass Sie in diesem Fall gegebenenfalls nicht sämtliche Funktionen dieser 
                  Website voll umfänglich nutzen können. Durch die Nutzung dieser Website erklären Sie sich mit der 
                  Bearbeitung der über Sie erhobenen Daten durch Google in der zuvor beschriebenen Art und Weise und zu 
                  dem zuvor benannten Zweck einverstanden.
                </p>
              </div>

              <div className="bg-background border border-foreground/10 rounded-3xl p-8">
                <h2 className="font-caslon text-xl font-bold mb-4">Google AdSense</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Diese Website benutzt Google Adsense, einen Webanzeigendienst der Google Inc., USA (&quot;Google&quot;). 
                  Google Adsense verwendet sog. &quot;Cookies&quot; (Textdateien), die auf Ihrem Computer gespeichert werden und 
                  die eine Analyse der Benutzung der Website durch Sie ermöglicht. Google Adsense verwendet auch sog. 
                  &quot;Web Beacons&quot; (kleine unsichtbare Grafiken) zur Sammlung von Informationen. Durch die Verwendung des 
                  Web Beacons können einfache Aktionen wie der Besucherverkehr auf der Webseite aufgezeichnet und 
                  gesammelt werden. Die durch den Cookie und/oder Web Beacon erzeugten Informationen über Ihre Benutzung 
                  dieser Website (einschließlich Ihrer IP-Adresse) werden an einen Server von Google in den USA übertragen 
                  und dort gespeichert. Google wird diese Informationen benutzen, um Ihre Nutzung der Website im Hinblick 
                  auf die Anzeigen auszuwerten, um Reports über die Websiteaktivitäten und Anzeigen für die Websitebetreiber 
                  zusammenzustellen und um weitere mit der Websitenutzung und der Internetnutzung verbundene Dienstleistungen 
                  zu erbringen. Auch wird Google diese Informationen gegebenenfalls an Dritte übertragen, sofern dies 
                  gesetzlich vorgeschrieben oder soweit Dritte diese Daten im Auftrag von Google verarbeiten. Google wird 
                  in keinem Fall Ihre IP-Adresse mit anderen Daten der Google in Verbindung bringen. Das Speichern von 
                  Cookies auf Ihrer Festplatte und die Anzeige von Web Beacons können Sie verhindern, indem Sie in Ihren 
                  Browser-Einstellungen &quot;keine Cookies akzeptieren&quot; wählen (Im MS Internet-Explorer unter Extras, 
                  Internetoptionen, Datenschutz, Einstellung; im Firefox unter Extras, Einstellungen, Datenschutz, 
                  Cookies); wir weisen Sie jedoch darauf hin, dass Sie in diesem Fall gegebenenfalls nicht sämtliche 
                  Funktionen dieser Website voll umfänglich nutzen können. Durch die Nutzung dieser Website erklären Sie 
                  sich mit der Bearbeitung der über Sie erhobenen Daten durch Google in der zuvor beschriebenen Art und 
                  Weise und zu dem zuvor benannten Zweck einverstanden.
                </p>
              </div>
            </div>

            {/* Back to Home */}
            <div className="mt-12 text-center">
              <a 
                href="/"
                className="inline-flex items-center gap-2 px-8 py-4 bg-foreground text-background rounded-2xl hover:bg-foreground/90 transition-all duration-300 hover:scale-105 font-medium"
              >
                Zurück zur Startseite
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Impressum;
